<template>
	<VerticalLayout>
		<Row :gutter="[16, 16]">
			<Col :span="18" :offset="0">
				<VerticalLayout>
					<StatsChartTitleWithDescription
						titleKey="stats.brdcm.coverHeatmap.chartTitle"
						titleClass="pd-lg-x pd-md-top"
					>
						<template #description>
							<BrdcmChartStatusHelp
								kind="cover"
								leadKey="stats.brdcm.coverHeatmap.chartTitleTooltip"
							/>
						</template>
					</StatsChartTitleWithDescription>
					<HeatMapChart
						v-if="datesRangeStr != null"
						:isLoading="isLoading"
						:series="coverHeatmapSeries"
						:categories="coverHeatmapCategories"
						colorSettingsMode="exact"
						:colorSettings="coverColorSettings"
						:noDataLabel="$t('stats.brdcm.legend.noData')"
						showLabelNamesInTooltip
						tooltip-wrap
						:tooltip-max-width="BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH"
						:xAxisLabel="$t('stats.brdcm.xAxisLabel')"
					/>
					<StatsChartTitleWithDescription
						titleKey="stats.brdcm.msmHeatmap.chartTitle"
						titleClass="pd-lg-x pd-md-top"
					>
						<template #description>
							<BrdcmChartStatusHelp
								kind="msm"
								leadKey="stats.brdcm.msmHeatmap.chartTitleTooltip"
							/>
						</template>
					</StatsChartTitleWithDescription>
					<HeatMapChart
						v-if="datesRangeStr != null"
						:isLoading="isLoading"
						:series="msmHeatmapSeries"
						:categories="msmHeatmapCategories"
						colorSettingsMode="exact"
						:colorSettings="msmColorSettings"
						:noDataLabel="$t('stats.brdcm.legend.noData')"
						showLabelNamesInTooltip
						tooltip-wrap
						:tooltip-max-width="BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH"
						:xAxisLabel="$t('stats.brdcm.xAxisLabel')"
					/>
				</VerticalLayout>
			</Col>
			<Col :span="6" :offset="0">
				<Card :bordered="false">
					<VerticalLayout>
						<div class="pd-vertical">
							<span>{{ $t('controls.gnssSelect.label') }}</span>
							<Select
								class="width-100"
								v-model:value="gnssSelected"
								mode="multiple"
								:allowClear="true"
								:options="gnssOptions"
							/>
						</div>
						<DateRangeChooserMaxSpan
							v-if="brdcmDateRangeLimit.enabled"
							v-model="datesRange"
							:maxSpanDays="brdcmDateRangeLimit.maxDays"
							@change="fetchStatsItems"
						/>
						<DateRangeChooser
							v-else
							v-model="datesRange"
							quickPeriod="day1"
							@change="fetchStatsItems"
						/>
					</VerticalLayout>
				</Card>
			</Col>
		</Row>
	</VerticalLayout>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Row, Col, Card, Select } from 'ant-design-vue';
import { useI18n } from 'vue-i18n';
import type { SelectOption } from '@/components/common/types/selectOptionTypes';

import Api from '@/api/Api';

import MapHelpers from '@/components/map/utils/MapHelpers';

import { useLocalDateRange } from '@/utils/useDateRangeAdapter';
import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import {
	BRDCM_GNSS_CODES,
	buildBrdcmCoverColorSettings,
	buildBrdcmMsmColorSettings,
	buildCoverChartItems,
	buildMsmChartItems,
	collectCoverSeriesKeys,
	collectMsmSeriesKeys,
	filterSatsByGnss,
} from '@/utils/brdcmHeatmapUtils';
import type {
	BrdcmHeatmapChartPointItem,
	BrdcmHeatmapTooltipLabels,
	BrdcmMergeItem,
	BrdcmStatsItem,
} from '@/utils/types/brdcmHeatmapTypes';
import type { HeatmapTooltipRow } from '@/utils/spoofingVisibilityHeatmapUtils';
import type { GenerateSeriesResult } from '@/utils/types/chartsUtilsTypes';

import { datetimeToString } from '@/components/common/dateUtils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';
import DateRangeChooserMaxSpan from '@/components/common/DateRangeChooserMaxSpan.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import BrdcmChartStatusHelp from '@/components/stats/BrdcmChartStatusHelp.vue';
import HeatMapChart from '@/components/charts/HeatMapChart.vue';


const BRDCM_DATE_RANGE_LIMIT = {
	enabled: true,
	maxDays: 5,
} as const;

const BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH = 280;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null && !Array.isArray(value);

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every(item => typeof item === 'string');

const isBrdcmStatsItem = (value: unknown): value is BrdcmStatsItem => {
	if (!isRecord(value)) {
		return false;
	}
	return typeof value.current_datetime === 'string'
		&& typeof value.satellite === 'string'
		&& (value.cover_class === null || value.cover_class === 'quorum'
			|| value.cover_class === 'held' || value.cover_class === 'weak')
		&& (value.stations_count === null || typeof value.stations_count === 'number')
		&& (value.quorum_count === null || typeof value.quorum_count === 'number')
		&& isStringArray(value.stations)
		&& isStringArray(value.msm_stations);
};

const isBrdcmMergeItem = (value: unknown): value is BrdcmMergeItem =>
	isRecord(value)
	&& typeof value.current_datetime === 'string'
	&& typeof value.merge_id === 'number'
	&& typeof value.status === 'string';

const isHeatmapTooltipRow = (value: unknown): value is HeatmapTooltipRow =>
	isRecord(value) && typeof value.label === 'string' && typeof value.value === 'string';


export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, DateRangeChooser, DateRangeChooserMaxSpan,
		StatsChartTitleWithDescription, BrdcmChartStatusHelp, HeatMapChart,
	},
	setup() {
		const { t } = useI18n();
		const { datesRange, datesRangeStr, datesRangeSafe } = useLocalDateRange(1, 0);
		const gnssSelected = ref<string[]>([]);
		const { apiData, isLoading, doRequest } = Api.useBrdcmStatsGet();

		const gnssOptions: SelectOption<string>[] = BRDCM_GNSS_CODES.map(code => ({
			value: code,
			label: MapHelpers.getGnssDataDisplay(code).name,
		}));

		const fetchStatsItems = () => {
			if (datesRangeStr.value == null) {
				return;
			}
			doRequest({
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
			});
		};

		onMounted(() => {
			fetchStatsItems();
		});

		const filteredItems = computed(() => {
			const rawItems: unknown = apiData.value.items;
			const items = Array.isArray(rawItems) ? rawItems.filter(isBrdcmStatsItem) : [];
			const allowedSats = new Set(filterSatsByGnss(
				items.map(item => item.satellite),
				gnssSelected.value,
			));
			return items.filter(item => allowedSats.has(item.satellite));
		});

		const merges = computed(() => {
			const rawMerges: unknown = apiData.value.merges;
			return Array.isArray(rawMerges) ? rawMerges.filter(isBrdcmMergeItem) : [];
		});

		const tooltipLabels = computed<BrdcmHeatmapTooltipLabels>(() => ({
			stations: t('stats.brdcm.tooltip.stations'),
			minimum: t('stats.brdcm.tooltip.minimum'),
			mergeId: t('stats.brdcm.tooltip.mergeId'),
			ephStations: t('stats.brdcm.tooltip.ephStations'),
			msmStations: t('stats.brdcm.tooltip.msmStations'),
		}));

		const coverChartItems = computed(() => buildCoverChartItems(
			filteredItems.value,
			merges.value,
			tooltipLabels.value,
		));

		const msmChartItems = computed(() => buildMsmChartItems(
			filteredItems.value,
			merges.value,
			tooltipLabels.value,
		));

		const coverSeriesKeys = computed(() => filterSatsByGnss(
			collectCoverSeriesKeys(filteredItems.value),
			gnssSelected.value,
		));

		const msmSeriesKeys = computed(() => filterSatsByGnss(
			collectMsmSeriesKeys(filteredItems.value),
			gnssSelected.value,
		));

		const buildHeatmapSeries = (
			chartItems: BrdcmHeatmapChartPointItem[],
			forceSeriesKeys: string[],
		): GenerateSeriesResult => {
			if (datesRangeSafe.value[0] == null || datesRangeSafe.value[1] == null) {
				return { series: [], categories: [] };
			}
			return generateSeries({
				items: chartItems,
				getSeriesKeyFn: item => String(item.satellite),
				getValueFn: item => (typeof item.status === 'number' ? item.status : null),
				seriesStep: apiData.value.period || 5,
				seriesStepUnit: 'minute',
				categoryFormatter: datetimeToString,
				pointEntryFactory: pointEntryFactoryXYDay,
				showNullAsZero: false,
				minDate: new Date(datesRangeSafe.value[0]),
				maxDate: new Date(datesRangeSafe.value[1]),
				forceSeriesKeys,
				roundTimeValues: true,
				pushAdditionalFields: ['tooltipRows'],
			});
		};

		const mapHeatmapSeries = (seriesCategories: GenerateSeriesResult) => seriesCategories.series.map(serie => ({
			name: serie.name,
			hidden: serie.hidden,
			data: serie.data.map(point => {
				if (Array.isArray(point)) {
					const emptyRows: HeatmapTooltipRow[] = [];
					return { x: point[0], y: point[1], data: emptyRows };
				}
				const tooltipRows = Array.isArray(point.tooltipRows)
					? point.tooltipRows.filter(isHeatmapTooltipRow)
					: [];
				return {
					x: point.x,
					y: point.y,
					data: tooltipRows,
				};
			}),
		}));

		const coverHeatmapSeriesCategories = computed(() => buildHeatmapSeries(
			coverChartItems.value,
			coverSeriesKeys.value,
		));

		const msmHeatmapSeriesCategories = computed(() => buildHeatmapSeries(
			msmChartItems.value,
			msmSeriesKeys.value,
		));

		const coverHeatmapSeries = computed(() => mapHeatmapSeries(coverHeatmapSeriesCategories.value));
		const msmHeatmapSeries = computed(() => mapHeatmapSeries(msmHeatmapSeriesCategories.value));
		const coverHeatmapCategories = computed(() => coverHeatmapSeriesCategories.value.categories.map(category => String(category)));
		const msmHeatmapCategories = computed(() => msmHeatmapSeriesCategories.value.categories.map(category => String(category)));

		const coverColorSettings = computed(() => buildBrdcmCoverColorSettings({
			quorum: t('stats.brdcm.legend.quorum'),
			weak: t('stats.brdcm.legend.weak'),
			held: t('stats.brdcm.legend.held'),
		}));

		const msmColorSettings = computed(() => buildBrdcmMsmColorSettings({
			quorum: t('stats.brdcm.legend.quorum'),
			weak: t('stats.brdcm.legend.weak'),
			held: t('stats.brdcm.legend.held'),
			msmNoEph: t('stats.brdcm.legend.msmNoEph'),
			ephNoMsm: t('stats.brdcm.legend.ephNoMsm'),
		}));

		return {
			BRDCM_HEATMAP_TOOLTIP_MAX_WIDTH,
			brdcmDateRangeLimit: BRDCM_DATE_RANGE_LIMIT,
			isLoading,
			datesRange,
			datesRangeStr,
			gnssSelected,
			gnssOptions,
			fetchStatsItems,
			coverHeatmapSeries,
			coverHeatmapCategories,
			msmHeatmapSeries,
			msmHeatmapCategories,
			coverColorSettings,
			msmColorSettings,
		};
	},
};
</script>
